import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, Database, Download, Filter, Search, BarChart3, FileText, Eye, Trophy, Plus, Edit2, Trash2, X, Save} from 'lucide-react'
import { useJerarquizacionCRUD } from '../hooks/useJerarquizacionCRUD'
import toast from 'react-hot-toast'

interface FormData {
  _id?: string
  codigo: string
  nombre_microcuenca: string
  municipio: string
  jerarquia: number
  prioridad: string
  ponderacion_final: number
  superficie_microcuenca: number
  superficie_agricola: number
  densidad_poblacion: number
  precipitacion: number
  temperatura: number
  deforestacion: number
  erosion: number
}

const DatabaseJerarquizacion = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPrioridad, setFilterPrioridad] = useState('Todas')
  const [selectedView, setSelectedView] = useState('tabla')
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<FormData | null>(null)
  const { data, loading, fetchData, createItem, updateItem, deleteItem } = useJerarquizacionCRUD()
  
  const [formData, setFormData] = useState<FormData>({
    codigo: '',
    nombre_microcuenca: '',
    municipio: '',
    jerarquia: 1,
    prioridad: 'Media',
    ponderacion_final: 0,
    superficie_microcuenca: 0,
    superficie_agricola: 0,
    densidad_poblacion: 0,
    precipitacion: 0,
    temperatura: 0,
    deforestacion: 0,
    erosion: 0
  })

  useEffect(() => {
    fetchData()
  }, [])

  const handleOpenModal = (item?: any) => {
    if (item) {
      setEditingItem(item)
      setFormData({
        _id: item._id,
        codigo: item.codigo || '',
        nombre_microcuenca: item.nombre_microcuenca || '',
        municipio: item.municipio || '',
        jerarquia: item.jerarquia || 1,
        prioridad: item.prioridad || 'Media',
        ponderacion_final: item.ponderacion_final || 0,
        superficie_microcuenca: item.superficie_microcuenca || 0,
        superficie_agricola: item.superficie_agricola || 0,
        densidad_poblacion: item.densidad_poblacion || 0,
        precipitacion: item.precipitacion || 0,
        temperatura: item.temperatura || 0,
        deforestacion: item.deforestacion || 0,
        erosion: item.erosion || 0
      })
    } else {
      setEditingItem(null)
      setFormData({
        codigo: '',
        nombre_microcuenca: '',
        municipio: '',
        jerarquia: 1,
        prioridad: 'Media',
        ponderacion_final: 0,
        superficie_microcuenca: 0,
        superficie_agricola: 0,
        densidad_poblacion: 0,
        precipitacion: 0,
        temperatura: 0,
        deforestacion: 0,
        erosion: 0
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingItem(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingItem) {
        await updateItem(editingItem._id!, formData)
      } else {
        await createItem(formData)
      }
      handleCloseModal()
      fetchData()
    } catch (error) {
      console.error('Error al guardar:', error)
    }
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de eliminar este registro?')) {
      try {
        await deleteItem(id)
        fetchData()
      } catch (error) {
        console.error('Error al eliminar:', error)
      }
    }
  }

  const filteredData = data.filter(cuenca => {
    const matchesSearch = 
      cuenca.nombre_microcuenca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cuenca.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cuenca.municipio?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterPrioridad === 'Todas' || cuenca.prioridad === filterPrioridad
    return matchesSearch && matchesFilter
  })

  const estadisticas = {
    totalCuencas: data.length,
    areaTotal: data.reduce((sum, c) => sum + (c.superficie_microcuenca || 0), 0),
    poblacionTotal: data.reduce((sum, c) => sum + (c.densidad_poblacion || 0), 0),
    municipiosUnicos: new Set(data.map(c => c.municipio)).size
  }

  const getRankingIcon = (jerarquia: number) => {
    if (jerarquia === 1) return '🥇'
    if (jerarquia === 2) return '🥈'
    if (jerarquia === 3) return '🥉'
    return jerarquia?.toString() || '-'
  }

  const getRankingColor = (jerarquia: number) => {
    if (jerarquia === 1) return 'bg-yellow-100 text-yellow-800 border-yellow-300'
    if (jerarquia === 2) return 'bg-gray-100 text-gray-800 border-gray-300'
    if (jerarquia === 3) return 'bg-orange-100 text-orange-800 border-orange-300'
    return 'bg-blue-100 text-blue-800 border-blue-300'
  }

  const exportToCSV = () => {
    const headers = ['Código', 'Nombre', 'Municipio', 'Jerarquía', 'Prioridad', 'Ponderación', 'Superficie (ha)', 'Densidad Población']
    const rows = filteredData.map(c => [
      c.codigo || '',
      c.nombre_microcuenca || '',
      c.municipio || '',
      c.jerarquia || '',
      c.prioridad || '',
      c.ponderacion_final?.toFixed(4) || '',
      c.superficie_microcuenca?.toFixed(2) || '',
      c.densidad_poblacion?.toFixed(2) || ''
    ])
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'jerarquizacion_microcuencas.csv'
    link.click()
  }

  if (loading && data.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Database className="w-12 h-12 text-blue-600 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600">Cargando datos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-lg border-b"
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver al Inicio</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                  <Database className="text-white w-4 h-4" />
                </div>
                <h1 className="text-xl font-bold text-gray-800">Jerarquización de Microcuencas</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => handleOpenModal()}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Nuevo Registro</span>
              </button>
              <button 
                onClick={exportToCSV}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Exportar CSV</span>
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="container mx-auto px-6 py-8">
        {/* Estadísticas Generales */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Microcuencas</p>
                <p className="text-3xl font-bold text-blue-600">{estadisticas.totalCuencas}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Área Total</p>
                <p className="text-3xl font-bold text-green-600">{estadisticas.areaTotal.toLocaleString('es-MX', { maximumFractionDigits: 0 })}</p>
                <p className="text-gray-500 text-xs">hectáreas</p>
              </div>
              <Database className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Densidad Total</p>
                <p className="text-3xl font-bold text-purple-600">{estadisticas.poblacionTotal.toLocaleString('es-MX', { maximumFractionDigits: 0 })}</p>
                <p className="text-gray-500 text-xs">población acum.</p>
              </div>
              <FileText className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Municipios</p>
                <p className="text-3xl font-bold text-orange-600">{estadisticas.municipiosUnicos}</p>
                <p className="text-gray-500 text-xs">únicos</p>
              </div>
              <Eye className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </motion.div>

        {/* Controles de Filtrado */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, código o municipio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-80"
                />
              </div>
              
              <select
                value={filterPrioridad}
                onChange={(e) => setFilterPrioridad(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Todas">Todas las prioridades</option>
                <option value="Muy Alta">Muy Alta</option>
                <option value="Alta">Alta</option>
                <option value="Media">Media</option>
                <option value="Baja">Baja</option>
                <option value="Muy Baja">Muy Baja</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSelectedView('tabla')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedView === 'tabla' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Tabla
              </button>
              <button
                onClick={() => setSelectedView('tarjetas')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedView === 'tarjetas' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Tarjetas
              </button>
            </div>
          </div>
        </motion.div>

        {/* Vista de Datos */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          {selectedView === 'tabla' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ranking</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Microcuenca</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Municipio</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prioridad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ponderación</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Superficie (ha)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((cuenca, index) => (
                    <motion.tr 
                      key={cuenca._id}
                      whileHover={{ backgroundColor: '#f9fafb' }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center justify-center w-10 h-8 border-2 rounded-full text-sm font-bold ${getRankingColor(cuenca.jerarquia || 0)}`}>
                          {getRankingIcon(cuenca.jerarquia || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{cuenca.codigo || '-'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{cuenca.nombre_microcuenca || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cuenca.municipio || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          cuenca.prioridad === 'Muy Alta' 
                            ? 'bg-red-100 text-red-800'
                            : cuenca.prioridad === 'Alta'
                            ? 'bg-orange-100 text-orange-800'
                            : cuenca.prioridad === 'Media'
                            ? 'bg-yellow-100 text-yellow-800'
                            : cuenca.prioridad === 'Baja'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {cuenca.prioridad || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {cuenca.ponderacion_final?.toFixed(4) || '-'}
                          </span>
                          {cuenca.ponderacion_final && (
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-green-400 to-red-500 h-2 rounded-full" 
                                style={{ width: `${Math.min((cuenca.ponderacion_final / 1.0) * 100, 100)}%` }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cuenca.superficie_microcuenca?.toLocaleString('es-MX', { maximumFractionDigits: 2 }) || '-'}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredData.map((cuenca) => (
                <motion.div
                  key={cuenca._id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:border-blue-300 transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`inline-flex items-center justify-center w-10 h-8 border-2 rounded-full text-sm font-bold ${getRankingColor(cuenca.jerarquia || 0)}`}>
                        {getRankingIcon(cuenca.jerarquia || 0)}
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      cuenca.prioridad === 'Muy Alta' 
                        ? 'bg-red-100 text-red-800'
                        : cuenca.prioridad === 'Alta'
                        ? 'bg-orange-100 text-orange-800'
                        : cuenca.prioridad === 'Media'
                        ? 'bg-yellow-100 text-yellow-800'
                        : cuenca.prioridad === 'Baja'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {cuenca.prioridad || '-'}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{cuenca.nombre_microcuenca || '-'}</h3>
                  <p className="text-sm text-gray-600 mb-1">Código: {cuenca.codigo || '-'}</p>
                  <p className="text-sm text-gray-600 mb-4">{cuenca.municipio || '-'}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ponderación:</span>
                      <span className="font-medium">{cuenca.ponderacion_final?.toFixed(4) || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Superficie:</span>
                      <span className="font-medium">{cuenca.superficie_microcuenca?.toLocaleString('es-MX', { maximumFractionDigits: 2 }) || '-'} ha</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Densidad Pob.:</span>
                      <span className="font-medium">{cuenca.densidad_poblacion?.toFixed(2) || '-'}</span>
                    </div>
                    {cuenca.precipitacion && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Precipitación:</span>
                        <span className="font-medium">{cuenca.precipitacion.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          
          {filteredData.length === 0 && (
            <div className="p-12 text-center text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No se encontraron microcuencas con los filtros aplicados</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default DatabaseJerarquizacion
